import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';
import { Loader } from 'components/Loader';

import { DATE_TIME_FORMAT } from '../../../../../constants/dates';
import STATUS from '../../../../../constants/status';
import { VisitorInvitationState } from '../../../../../store/slices/visitorInvitationSlice';
import { LocationsState } from '../../../../../store/slices/locationsSlice';
import { ImmutableRootState } from '../../../../../store/store';
import { VisitorSearch } from '../../../../../types/visitorSearchTypes';
import { VisitorWorkflowConfig } from '../../../../../types/visitorWorkflowTypes';

interface SignInMethodModalProps {
  open: boolean;
  onClose: () => void;
  openSignInModal: () => void;
  openScanIDModal: () => void;
  openScanQRCodeModal: () => void;
  openSignInManuallyDrawer: () => void;
  locationIdRef: number;
  loading?: boolean;
  signInWorkflowConfig?: VisitorWorkflowConfig;
}

interface SearchVisitorsResponse {
  data?: VisitorSearch[] | null;
}

const SignInMethodModal = ({
  open,
  onClose,
  openSignInModal,
  openScanIDModal,
  openScanQRCodeModal,
  openSignInManuallyDrawer,
  locationIdRef,
  loading,
  signInWorkflowConfig,
}: SignInMethodModalProps) => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();

  const invitationConfig = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visitorInvitation', 'invitationConfig']),
  ) as VisitorInvitationState['invitationConfig'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  return (
    <OpModal
      title={t('signInMethodModal.title')}
      open={open}
      onOk={onClose}
      onCancel={onClose}
      centered
      width={720}
      footer={null}
    >
      <OpSpace
        orientation="vertical"
        size="large"
        style={{ display: 'flex', paddingTop: '16px' }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader inline />
          </div>
        ) : (
          <>
            <OpButton
              type="default"
              style={{ width: '100%', backgroundColor: 'rgba(19,151,213,0.1)' }}
              onClick={async () => {
                try {
                  const selectedLocation = locations.find(
                    (loc) => loc.id === locationIdRef,
                  );
                  const tz = selectedLocation?.timezone || 'UTC';
                  const startDate = dayjs()
                    .tz(tz)
                    .startOf('day')
                    .utc()
                    .format(DATE_TIME_FORMAT);
                  const endDate = dayjs()
                    .tz(tz)
                    .endOf('day')
                    .utc()
                    .format(DATE_TIME_FORMAT);

                  const response = (await heliumReceive(
                    'getSearchVisitors',
                    [orgId],
                    {
                      queryStringParams: {
                        locationId: locationIdRef,
                        startDate,
                        endDate,
                      },
                    },
                  )) as unknown as SearchVisitorsResponse;

                  const pendingVisitors = (response.data ?? []).filter(
                    (visitor) => visitor.status === STATUS.PENDING.id,
                  );

                  if (pendingVisitors.length === 0) {
                    // If there are no pending visits → open the “Add Visitor” modal and drawer
                    openSignInManuallyDrawer();
                  } else {
                    openSignInModal();
                  }
                } catch {
                  openSignInManuallyDrawer();
                }
                onClose();
              }}
            >
              {t('signInMethodModal.selectOrEnter')}
            </OpButton>
            {signInWorkflowConfig?.scan === 1 &&
              signInWorkflowConfig?.scanScanner === 1 && (
                <OpButton
                  type="default"
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(19,151,213,0.1)',
                  }}
                  onClick={() => {
                    openScanIDModal();
                    onClose();
                  }}
                >
                  {t('signInMethodModal.scanId')}
                </OpButton>
              )}
            {invitationConfig?.allowPreRegistration === 1 && (
              <OpButton
                type="default"
                style={{
                  width: '100%',
                  backgroundColor: 'rgba(19,151,213,0.1)',
                }}
                onClick={() => {
                  openScanQRCodeModal();
                  onClose();
                }}
              >
                {t('signInMethodModal.scanQrCode')}
              </OpButton>
            )}
          </>
        )}
      </OpSpace>
    </OpModal>
  );
};

export default SignInMethodModal;
